import { Dropdown, Tooltip } from 'antd';
import { ClickParam } from 'antd/lib/menu';
import clevertap from 'clevertap-web-sdk';
import { History } from 'history';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { LDProps } from 'launchdarkly-react-client-sdk/lib/withLDConsumer';
import moment from 'moment';
import queryString from 'query-string';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Form, InputOnChangeData } from 'semantic-ui-react';

import {
    navigationService,
    toastNotificationsService,
    userAuthenticator,
} from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { CountryInput } from 'src/shared/components/CountryInput';
import { DisclaimerModal } from 'src/shared/components/DisclaimerModal';
import {
    containsNumber,
    containsSpecialCharacter,
    containsUppercaseLetter,
} from 'src/shared/helpers/characterContainValidators';
import { isEmailValid } from 'src/shared/helpers/emailValidator';
import { isLastMandatoryTerm } from 'src/shared/helpers/isLastMandatoryTerm';
import { checkNameValidity } from 'src/shared/helpers/isNameValid';
import { EapAuthLayout } from 'src/shared/layout/EapAuthLayout';
import { EuropeanCountryCodes } from 'src/shared/models/shared';

import { AuthorizationBox } from '../components/AuthorizationBox';
import { PasswordValidationInfo } from '../components/PasswordValidationInfo';
import { ConsentTermsKorea } from './ConsentTermsKorea';
import { ConsentTermsMain } from './ConsentTermsMain';
import * as S from './styles';

interface MatchParams {
    uidb64: string;
}

type Props = ReturnType<typeof mapDispatchToProps> &
    RouteComponentProps<MatchParams> &
    LDProps &
    WithTranslation & { history: History };

interface State extends Record<string, any> {
    showDisclaimerModal: boolean;
    registrationInProgress: boolean;
    termsAccepted: boolean;
    privacyAccepted: boolean;
    personalHealthAccepted: boolean;
    consentToAllAccepted: boolean;
    collectAndUseSensitiveInfoAccepted: boolean;
    provisionOfPersonalInfoUseAccepted: boolean;
    overseasPersonalInfoXferAccepted: boolean;
    collectPersonalInfoForMarketingAccepted: boolean;
    firstName: string;
    lastName: string;
    company: string;
    email: string;
    password: string;
    passwordConf: string;
    dateOfBirth: string;
    dateWithWrongFormat: string;
    registrationToken: string | undefined;
    serverSideErrors: string[];
    touched: Record<string, boolean>;
}

export enum RegistrationFormTestId {
    FirstNameField = 'RegistrationFormTestId.FirstNameField',
    LastNameField = 'RegistrationFormTestId.LastNameField',
    Company = 'RegistrationFormTestId.Company',
    EmailField = 'RegistrationFormTestId.EmailField',
    PasswordField = 'RegistrationFormTestId.PasswordField',
    ConfirmPasswordField = 'RegistrationFormTestId.ConfirmPasswordField',
    TermsCheckBox = 'RegistrationFormTestId.TermsCheckBox',
    PrivacyCheckBox = 'RegistrationFormTestId.PrivacyCheckBox',
    PersonalHealthInfoCheckBox = 'RegistrationFormTestId.PersonalHealthInfoCheckBox',
}

enum Company {
    EA = 'ea',
    ELECTRONIC_ARTS = 'electronic arts',
}

export enum Country {
    CHINA = 'China',
    SOUTH_KOREA = 'South Korea',
}

export enum KoreaTerms {
    CONSENT_TO_ALL_ACCEPTED = 'consentToAllAccepted',
    TERMS = 'termsAccepted',
    PRIVACY = 'privacyAccepted',
    USE_SENSITIVE_INFO = 'collectAndUseSensitiveInfoAccepted',
    PROVISION_PERSONAL_INFO = 'provisionOfPersonalInfoUseAccepted',
    OVERSEAS_XFER_PERSONAL_INFO = 'overseasPersonalInfoXferAccepted',
    OPTIONAL_MARKETING_INFO_USE_ACCEPTED = 'collectPersonalInfoForMarketingAccepted',
}

class RegistrationForm extends React.Component<Props, State> {
    public state: State = {
        showDisclaimerModal: false,
        registrationInProgress: false,
        termsAccepted: false, // shared tos
        privacyAccepted: false, // shared privacy policy
        personalHealthAccepted: false, // euro only
        consentToAllAccepted: false, // korea only
        collectAndUseSensitiveInfoAccepted: false, // korea only
        provisionOfPersonalInfoUseAccepted: false, // korea only
        overseasPersonalInfoXferAccepted: false, // korea only
        collectPersonalInfoForMarketingAccepted: false, // korea only
        firstName: '',
        lastName: '',
        company: '',
        country: 'United States',
        email: '',
        password: '',
        passwordConf: '',
        dateOfBirth: '',
        dateWithWrongFormat: '',
        registrationToken: undefined,
        serverSideErrors: [],
        touched: {},
        monthOfBirth: '',
        dayOfBirth: '',
        yearOfBirth: '',
        daysInMonth: 0,
    };

    public componentDidMount(): void {
        const searchParams = queryString.parse(this.props.location.search);

        const registrationToken =
            (searchParams?.['registration_token'] as string) || this.props.match.params.uidb64;

        const companyCode = (searchParams?.['company_code'] as string) || '';

        // clears params from url
        window.history.replaceState(null, '', window.location.pathname);

        this.setState({ registrationToken, company: companyCode });
    }

    private toggleCheckbox = (type: string) => {
        this.setState(state => ({ [type]: !state[type] }));
    };

    private isFormValid = () => {
        return (
            this.state.country &&
            this.isPasswordValid() &&
            this.is18OrOlder() &&
            this.arePasswordsTheSame() &&
            isEmailValid(this.state.email) &&
            checkNameValidity(this.state.firstName.trim()).isValid &&
            checkNameValidity(this.state.lastName.trim()).isValid &&
            this.state.dateOfBirth &&
            this.state.company.trim()
        );
    };

    private handleSubmit = async () => {
        if (!this.isFormValid()) {
            this.setState({
                touched: {
                    firstName: true,
                    lastName: true,
                    company: true,
                    email: true,
                    password: true,
                    passwordConf: true,
                    dateOfBirth: true,
                },
            });

            return;
        }

        this.setState({
            serverSideErrors: [],
        });

        const { company, country, dateOfBirth } = this.state;

        const dateOfBirthISO = dateOfBirth ? this.formatDOB(dateOfBirth) : '';

        if (dateOfBirthISO === 'Invalid date') {
            this.setState({ dateWithWrongFormat: dateOfBirth });

            toastNotificationsService.renderErrorToast(
                'auth:registration_form.errors.wrongDateFormat',
            );

            return;
        }

        // EA China registors must accept disclaimer policies 04/13/21
        if (this.shouldCompanyNeedDisclaimer(company) && country === Country.CHINA) {
            this.setState({ showDisclaimerModal: true });
        } else {
            this.submitRegistrationForm();
        }
    };

    private shouldCompanyNeedDisclaimer = (company: string) => {
        return [Company.EA, Company.ELECTRONIC_ARTS].some(name => name === company.toLowerCase());
    };

    private formatDOB = (dob: string) => {
        return moment(dob, 'MM-DD-YYYY').format('YYYY-MM-DD');
    };

    private submitRegistrationForm = async (): Promise<void> => {
        const {
            firstName,
            lastName,
            email,
            password,
            company,
            country,
            registrationToken,
            dateOfBirth,
        } = this.state;

        const commonCredentials = {
            email,
            password,
            firstName,
            lastName,
            dateOfBirth: this.formatDOB(dateOfBirth),
        };

        try {
            this.setState({ registrationInProgress: true });

            await userAuthenticator.createAccount({
                ...commonCredentials,
                clevertapId: clevertap.getCleverTapID(),
                company: company.toLowerCase(),
                country,
                registrationToken,
            });

            this.props.login({ email, password });
        } catch (errors) {
            this.setState({ serverSideErrors: errors });

            if (errors.includes('INELIGIBLE')) {
                this.setState({ registrationInProgress: false, showDisclaimerModal: false });
                navigationService.navigate('/ineligible');
            }
        } finally {
            this.setState({ registrationInProgress: false, showDisclaimerModal: false });
        }
    };

    private handleAcceptDisclaimer = async () => {
        this.submitRegistrationForm();
    };

    private isPasswordValid = (password: string = this.state.password): boolean =>
        containsSpecialCharacter(password) &&
        containsUppercaseLetter(password) &&
        containsNumber(password) &&
        password.trim().length >= 12;

    private arePasswordsTheSame = (): boolean =>
        this.state.password.trim() == this.state.passwordConf.trim();

    private is18OrOlder = (): boolean => {
        if (!this.state.dateOfBirth) {
            return false;
        }

        return moment().diff(moment(this.state.dateOfBirth, 'MM-DD-YYYY'), 'years') >= 18;
    };

    private isTouchedAndMissing = (name: string): boolean => {
        const value = this.state[name];

        return typeof value == 'string'
            ? !value.trim() && !!this.state.touched[name]
            : !value && !!this.state.touched[name];
    };

    private handleBlur = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) =>
        this.setState(state => ({ touched: { ...state.touched, [currentTarget.name]: true } }));

    private handleChange = (
        _event: React.SyntheticEvent<HTMLInputElement>,
        target: InputOnChangeData,
    ) =>
        this.setState(state => ({
            [target.name]: target.value,
            serverSideErrors: state.serverSideErrors.filter(error => error != target.name),
        }));

    private handleModeChange = () => navigationService.navigate('/login');

    private handleYearChange = (e: ClickParam) => {
        if (!this.state.monthOfBirth) {
            this.setState({ yearOfBirth: e.key }, this.setFullDate);
        } else {
            const daysCount = this.daysInMonth(this.state.monthOfBirth, e.key);
            this.setState(
                { yearOfBirth: e.key, daysInMonth: daysCount, dayOfBirth: '' },
                this.setFullDate,
            );
        }
    };

    private handleMonthChange = (e: ClickParam) => {
        if (!this.state.yearOfBirth) {
            this.setState({ monthOfBirth: e.key, dayOfBirth: '' }, this.setFullDate);
        } else {
            const daysCount = this.daysInMonth(e.key, this.state.yearOfBirth);
            this.setState(
                { monthOfBirth: e.key, daysInMonth: daysCount, dayOfBirth: '' },
                this.setFullDate,
            );
        }
    };

    private daysInMonth = (month: string, year: string) => {
        return moment(month + '-' + year, 'MMMM-YYYY').daysInMonth();
    };

    private handleDayChange = (e: ClickParam) => {
        this.setState(
            state => ({ dayOfBirth: e.key, touched: { ...state.touched, dateOfBirth: true } }),
            this.setFullDate,
        );
    };

    private setFullDate = () => {
        const { dayOfBirth, monthOfBirth, yearOfBirth } = this.state;
        if (!(dayOfBirth && monthOfBirth && yearOfBirth)) {
            return;
        }
        const date = moment(
            monthOfBirth + '-' + dayOfBirth + '-' + yearOfBirth,
            'MMMM-DD-YYYY',
        ).format('MM-DD-YYYY');
        this.setState({ dateOfBirth: date });
    };

    private areKnMandatoryTermsAccepted = (): boolean => {
        const {
            termsAccepted,
            privacyAccepted,
            collectAndUseSensitiveInfoAccepted,
            provisionOfPersonalInfoUseAccepted,
            overseasPersonalInfoXferAccepted,
        } = this.state;
        return (
            termsAccepted &&
            privacyAccepted &&
            collectAndUseSensitiveInfoAccepted &&
            provisionOfPersonalInfoUseAccepted &&
            overseasPersonalInfoXferAccepted
        );
    };

    private toggleConsentToAll = (): void => {
        this.setState(state => ({
            consentToAllAccepted: !state.consentToAllAccepted,
            termsAccepted: !state.consentToAllAccepted,
            privacyAccepted: !state.consentToAllAccepted,
            collectAndUseSensitiveInfoAccepted: !state.consentToAllAccepted,
            provisionOfPersonalInfoUseAccepted: !state.consentToAllAccepted,
            overseasPersonalInfoXferAccepted: !state.consentToAllAccepted,
            collectPersonalInfoForMarketingAccepted: !state.consentToAllAccepted,
        }));
    };

    private handleAcceptOptionalMarketing = (): void => {
        this.setState(state => ({
            consentToAllAccepted: this.areKnMandatoryTermsAccepted()
                ? !state.collectPersonalInfoForMarketingAccepted
                : state.consentToAllAccepted,
            collectPersonalInfoForMarketingAccepted: !state.collectPersonalInfoForMarketingAccepted,
        }));
    };

    private handleAutoToggle = (
        hash: Record<string, boolean | string>,
        key: string,
    ): Record<string, boolean> => {
        const {
            consentToAllAccepted,
            collectPersonalInfoForMarketingAccepted: optional,
        } = this.state;
        const shouldIncludeConsentToAll =
            !consentToAllAccepted && isLastMandatoryTerm(hash, key) && optional;
        const shouldIncludeOptional =
            consentToAllAccepted && isLastMandatoryTerm(hash, key) && !optional;
        return {
            shouldIncludeConsentToAll,
            shouldIncludeOptional,
        };
    };

    private handleAcceptTerms = (hash: Record<string, boolean | string>, key: string): void => {
        const { shouldIncludeConsentToAll, shouldIncludeOptional } = this.handleAutoToggle(
            hash,
            key,
        );
        this.setState(state => ({
            consentToAllAccepted: shouldIncludeConsentToAll
                ? !state.consentToAllAccepted
                : state.consentToAllAccepted,
            [key]: !state[key],
            collectPersonalInfoForMarketingAccepted: shouldIncludeOptional
                ? !state.collectPersonalInfoForMarketingAccepted
                : state.collectPersonalInfoForMarketingAccepted,
        }));
    };

    private updateTermAcceptance = (termName: string): void => {
        switch (termName) {
            case KoreaTerms.CONSENT_TO_ALL_ACCEPTED:
                return this.toggleConsentToAll();
            case KoreaTerms.OPTIONAL_MARKETING_INFO_USE_ACCEPTED:
                return this.handleAcceptOptionalMarketing();
            default:
                return this.handleAcceptTerms(this.state, termName);
        }
    };

    public render(): JSX.Element {
        const {
            serverSideErrors,
            registrationInProgress,
            showDisclaimerModal,

            // form state
            firstName,
            lastName,
            dateOfBirth,
            dayOfBirth,
            yearOfBirth,
            monthOfBirth,
            daysInMonth,
            dateWithWrongFormat,
            email,
            password,
            passwordConf,
            company,
            country,
            touched,

            // consent box states
            consentToAllAccepted,
            termsAccepted,
            privacyAccepted,
            personalHealthAccepted,
            collectAndUseSensitiveInfoAccepted,
            provisionOfPersonalInfoUseAccepted,
            overseasPersonalInfoXferAccepted,
            collectPersonalInfoForMarketingAccepted,
        } = this.state;

        const { t, flags } = this.props;

        const emailTaken = serverSideErrors.includes('email');
        const companyNotFound = serverSideErrors.includes('company');

        const missingCompany = this.isTouchedAndMissing('company');
        const missingEmail = this.isTouchedAndMissing('email');
        const missingPassword = this.isTouchedAndMissing('password');
        const missingPasswordConf = this.isTouchedAndMissing('passwordConf');
        const missingDOB = this.isTouchedAndMissing('dateOfBirth');

        const tooYoungToRegister = touched['dateOfBirth'] && !this.is18OrOlder();

        const invalidEmail = !missingEmail && !isEmailValid(email) && touched.email;
        const invalidPassword = !missingPassword && !this.isPasswordValid() && touched.password;
        const invalidPasswordConf =
            !missingPasswordConf && !this.isPasswordValid(passwordConf) && touched.passwordConf;

        const companyError = companyNotFound || missingCompany;
        const emailError = emailTaken || invalidEmail || missingEmail;
        const passwordError = missingPassword || invalidPassword;
        const passwordConfError = missingPasswordConf || invalidPasswordConf;
        const passwordsDontMatch = !this.arePasswordsTheSame() && touched.passwordConf;

        const {
            isValid: isFirstNameValid,
            invalidCharacters: firstNameInvalidCharacters,
        } = checkNameValidity(firstName);
        const {
            isValid: isLastNameValid,
            invalidCharacters: lastNameInvalidCharacters,
        } = checkNameValidity(lastName);

        const wrongDateFormat = !!dateOfBirth && dateOfBirth === dateWithWrongFormat;

        const years: string[] = [];
        const dateEnd = moment().subtract(90, 'years');
        const dateStart = moment().subtract(18, 'years');
        while (dateEnd.diff(dateStart, 'years') <= 0) {
            years.push(dateStart.format('YYYY'));
            dateStart.subtract(1, 'year');
        }

        const yearListItems = years.map(year => (
            <S.MenuItem key={year} onClick={this.handleYearChange}>
                <S.MenuItemText>{year}</S.MenuItemText>
            </S.MenuItem>
        ));

        const monthsListItems = moment.months().map(month => {
            return (
                <S.MenuItem key={month} onClick={this.handleMonthChange}>
                    <S.MenuItemText>{month}</S.MenuItemText>
                </S.MenuItem>
            );
        });

        const daysList = Array.from({ length: this.state.daysInMonth }, (_, i) => i + 1);
        const daysListItems = daysList.map(day => (
            <S.MenuItem key={day} onClick={this.handleDayChange}>
                <S.MenuItemText>{day}</S.MenuItemText>
            </S.MenuItem>
        ));

        const isEuropeanCountry = EuropeanCountryCodes[country] !== undefined;
        const showAdditionalCheckboxesForKorea =
            country === Country.SOUTH_KOREA && flags?.showKnTerms;
        const hideTosPrivacyUpdateMemberWeb = flags?.hideTosPrivacyUpdateMemberWeb;
        const showEuPersonalHealthInfoTerm = flags?.showEuPersonalHealthInfoTerm;

        const formIsValid = isEuropeanCountry
            ? termsAccepted &&
              privacyAccepted &&
              ((showEuPersonalHealthInfoTerm && personalHealthAccepted) || true)
            : showAdditionalCheckboxesForKorea
            ? this.areKnMandatoryTermsAccepted()
            : !showAdditionalCheckboxesForKorea &&
              !isEuropeanCountry &&
              termsAccepted &&
              privacyAccepted;

        return (
            <EapAuthLayout>
                <AuthorizationBox
                    title={t('auth:registration_form.title')}
                    subTitle={t('auth:registration_form.subtitle')}
                    buttonText={t('auth:registration_form.buttonText')}
                    questionText={t('auth:registration_form.questionText')}
                    needHelp={t('auth:registration_form.needHelp')}
                    questionButtonText={t('auth:registration_form.questionButtonText')}
                    onSingleSignOnRequest={() => navigationService.navigate('/single-sign-on')}
                    onModeChange={this.handleModeChange}
                    isFetching={registrationInProgress}
                    isFormValid={hideTosPrivacyUpdateMemberWeb ? termsAccepted : formIsValid}
                    onSubmit={this.handleSubmit}
                >
                    <>
                        <Tooltip
                            placement="top"
                            title={t('auth:registration_form.fullNameTooltipInfo')}
                            trigger="focus"
                        >
                            <Form.Group widths="equal">
                                <S.Input
                                    required
                                    fluid
                                    data-testid={RegistrationFormTestId.FirstNameField}
                                    name="firstName"
                                    label={t('auth:registration_form.labels.firstName')}
                                    error={!isFirstNameValid && this.state.touched.firstName}
                                    value={this.state.firstName}
                                    onChange={this.handleChange}
                                    onBlur={this.handleBlur}
                                    icon={!isFirstNameValid ? 'warning sign' : ''}
                                />
                                <S.Input
                                    required
                                    fluid
                                    data-testid={RegistrationFormTestId.LastNameField}
                                    name="lastName"
                                    label={t('auth:registration_form.labels.lastName')}
                                    value={this.state.lastName}
                                    error={!isLastNameValid && touched.lastName}
                                    onChange={this.handleChange}
                                    onBlur={this.handleBlur}
                                    icon={!isLastNameValid ? 'warning sign' : ''}
                                />
                            </Form.Group>
                            {(!isFirstNameValid || !isLastNameValid) && (
                                <S.Message error>
                                    {t('auth:registration_form.errors.invalidName', {
                                        invalidCharacters: firstNameInvalidCharacters
                                            .concat(lastNameInvalidCharacters)
                                            .join(', '),
                                    })}
                                </S.Message>
                            )}
                        </Tooltip>

                        <S.FormField
                            required
                            error={missingDOB || wrongDateFormat || tooYoungToRegister}
                        >
                            <label>{t('auth:registration_form.labels.dateOfBirth')}</label>
                            <Dropdown
                                overlay={<S.MenuWrapper>{yearListItems}</S.MenuWrapper>}
                                trigger={['click']}
                            >
                                <S.DropdownWrapper>
                                    {yearOfBirth ||
                                        t('auth:registration_form.labels.dateOfBirth.year')}
                                </S.DropdownWrapper>
                            </Dropdown>
                            <Dropdown
                                overlay={<S.MenuWrapper>{monthsListItems}</S.MenuWrapper>}
                                trigger={['click']}
                            >
                                <S.DropdownWrapper>
                                    {monthOfBirth ||
                                        t('auth:registration_form.labels.dateOfBirth.month')}
                                </S.DropdownWrapper>
                            </Dropdown>
                            <Dropdown
                                overlay={<S.MenuWrapper>{daysListItems}</S.MenuWrapper>}
                                trigger={['click']}
                                disabled={daysInMonth == 0}
                            >
                                <S.DropdownWrapper>
                                    {dayOfBirth ||
                                        t('auth:registration_form.labels.dateOfBirth.day')}
                                </S.DropdownWrapper>
                            </Dropdown>
                        </S.FormField>

                        {missingDOB ? (
                            <S.Message error>
                                {t('auth:registration_form.errors.missing_dob')}
                            </S.Message>
                        ) : tooYoungToRegister ? (
                            <S.Message error>
                                {t('auth:registration_form.errors.underaged')}
                            </S.Message>
                        ) : null}
                        <CountryInput
                            onSelectCountry={(value: string) => {
                                this.setState({
                                    ...this.state,
                                    country: value,
                                });
                            }}
                            country={country}
                        />
                        <S.Input
                            required
                            fluid
                            data-testid={RegistrationFormTestId.Company}
                            label={t('auth:registration_form.labels.company')}
                            name="company"
                            value={company}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={companyError}
                            icon={companyError ? 'warning sign' : ''}
                        />
                        {companyNotFound ? (
                            <S.Message error>
                                {t('auth:registration_form.errors.companyNotFound')}
                                <a href="mailto:help@joinmodernhealth.com">
                                    {' '}
                                    help@joinmodernhealth.com
                                </a>
                            </S.Message>
                        ) : (
                            missingCompany && (
                                <S.Message error>
                                    {t('auth:registration_form.errors.companyMissing')}
                                </S.Message>
                            )
                        )}

                        <S.Input
                            required
                            label={t('auth:registration_form.labels.email')}
                            type="email"
                            data-testid={RegistrationFormTestId.EmailField}
                            name="email"
                            autoComplete="username"
                            placeholder={t('auth:registration_form.placeholders.email')}
                            value={email}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={emailError}
                            icon={emailError ? 'warning sign' : ''}
                        />
                        {emailTaken ? (
                            <S.Message error>
                                {t('auth:registration_form.errors.emailTaken')}
                            </S.Message>
                        ) : (
                            <>
                                {missingEmail && (
                                    <S.Message error>
                                        {t('auth:registration_form.errors.emailMissing')}
                                    </S.Message>
                                )}
                                {invalidEmail && (
                                    <S.Message error>
                                        {t('auth:registration_form.errors.emailInvalid')}
                                    </S.Message>
                                )}
                            </>
                        )}
                        <S.Input
                            fluid
                            required
                            pattern=".{6,}"
                            data-testid={RegistrationFormTestId.PasswordField}
                            label={t('auth:registration_form.labels.password')}
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={passwordError}
                            icon={passwordError ? 'warning sign' : ''}
                        />
                        {(touched.password || password) && !this.isPasswordValid() && (
                            <PasswordValidationInfo
                                containsSpecialCharacter={containsSpecialCharacter(password)}
                                containsUppercaseLetter={containsUppercaseLetter(password)}
                                containsNumber={containsNumber(password)}
                                isLongEnough={password.trim().length >= 12}
                            />
                        )}
                        <S.Input
                            fluid
                            required
                            pattern=".{6,}"
                            data-testid={RegistrationFormTestId.ConfirmPasswordField}
                            label={t('auth:registration_form.labels.confirmPassword')}
                            name="passwordConf"
                            type="password"
                            autoComplete="new-password"
                            value={passwordConf}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={passwordConfError || passwordsDontMatch}
                            icon={passwordConfError || passwordsDontMatch ? 'warning sign' : ''}
                        />
                        {missingPasswordConf && (
                            <S.Message error>
                                {t('auth:registration_form.errors.passwordConfirmationMissing')}
                            </S.Message>
                        )}
                        {passwordsDontMatch && (
                            <S.Message error>
                                {t('auth:registration_form.errors.passwordsDontMatch')}
                            </S.Message>
                        )}

                        {!showAdditionalCheckboxesForKorea && (
                            <ConsentTermsMain
                                hideTosPrivacyUpdateMemberWeb={hideTosPrivacyUpdateMemberWeb}
                                showEuPersonalHealthInfoTerm={showEuPersonalHealthInfoTerm}
                                isEuropeanCountry={isEuropeanCountry}
                                onChange={this.toggleCheckbox}
                                termsAccepted={termsAccepted}
                                privacyAccepted={privacyAccepted}
                                personalHealthAccepted={personalHealthAccepted}
                            />
                        )}

                        {showAdditionalCheckboxesForKorea && (
                            <ConsentTermsKorea
                                onChange={this.updateTermAcceptance}
                                consentToAllAccepted={consentToAllAccepted}
                                termsAccepted={termsAccepted}
                                privacyAccepted={privacyAccepted}
                                collectAndUseSensitiveInfoAccepted={
                                    collectAndUseSensitiveInfoAccepted
                                }
                                provisionOfPersonalInfoUseAccepted={
                                    provisionOfPersonalInfoUseAccepted
                                }
                                overseasPersonalInfoXferAccepted={overseasPersonalInfoXferAccepted}
                                collectPersonalInfoForMarketingAccepted={
                                    collectPersonalInfoForMarketingAccepted
                                }
                            />
                        )}

                        <DisclaimerModal
                            visible={showDisclaimerModal}
                            description={t('shared:disclaimer_modal.description', {
                                returnObjects: true,
                            })}
                            onClick={this.handleAcceptDisclaimer}
                            buttonText={t('shared:disclaimer_modal.buttonText')}
                        />
                    </>
                </AuthorizationBox>
            </EapAuthLayout>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
    login: dispatch.authentication.login,
});

export default withLDConsumer()(
    withTranslation()(connect(null, mapDispatchToProps)(RegistrationForm)),
);
